import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { LogoMark } from "./Logo";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About Us", id: "about" },
  { to: "/services", label: "Our Services", id: "services" },
  { to: "/how-it-works", label: "How It Works", id: "how-it-works" },
  { to: "/why-choose-us", label: "Why Choose Us", id: "why-choose-us" },
  { to: "/contact", label: "Contact Us", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="nav-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0D]/80 border-b border-[rgba(212,175,55,0.15)]"
    >
      <div className="max-w-7xl mx-auto h-20 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
          <LogoMark size={38} />
          <span className="leading-none">
            <span className="font-display font-bold text-lg tracking-[0.14em] gold-text gold-shimmer block">CREDNOVA</span>
            <span className="font-mono-brand text-[9px] tracking-[0.32em] text-[#A1A1AA] block mt-1">SERVICES PVT. LTD.</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors duration-200 ${
                  isActive ? "text-[#E6CA65]" : "text-[#A1A1AA] hover:text-[#F4F4F6]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-get-loan-assistance"
            className="btn-gold hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"
          >
            <span className="relative z-10 flex items-center gap-2">
              Get Loan Assistance <ArrowRight size={15} />
            </span>
          </Link>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="hidden text-[#E6CA65] p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="lg:hidden overflow-hidden border-t border-[rgba(212,175,55,0.15)] bg-[#0A0A0D]/95"
            aria-label="Mobile"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-3 text-sm border-b border-[rgba(212,175,55,0.08)] ${
                      isActive ? "text-[#E6CA65]" : "text-[#A1A1AA]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-get-loan-assistance"
                onClick={() => setOpen(false)}
                className="btn-gold mt-4 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get Loan Assistance <ArrowRight size={15} />
                </span>
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
